import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mlm/ui';
import {
  cancelPayout,
  getPayoutMethods,
  getWallet,
  listPayouts,
  previewPayout,
  requestPayout,
} from '../lib/api';

export function PayoutsPage() {
  const qc = useQueryClient();
  const { data: wallet } = useQuery({ queryKey: ['merchant-wallet'], queryFn: getWallet });
  const { data: methodsData } = useQuery({
    queryKey: ['merchant-payout-methods'],
    queryFn: getPayoutMethods,
  });
  const { data: requests = [] } = useQuery({
    queryKey: ['merchant-payouts'],
    queryFn: listPayouts,
  });
  const methods = methodsData?.methods ?? [];

  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('');
  const [account, setAccount] = useState('');
  const [preview, setPreview] = useState<Awaited<ReturnType<typeof previewPayout>> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!amount || !method) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    previewPayout(amount, method)
      .then((p) => !cancelled && setPreview(p))
      .catch(() => !cancelled && setPreview(null));
    return () => {
      cancelled = true;
    };
  }, [amount, method]);

  const submit = useMutation({
    meta: { successMessage: 'Payout requested.' },
    mutationFn: () => requestPayout({ amount, method, payload: account ? { account } : undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['merchant-payouts'] });
      qc.invalidateQueries({ queryKey: ['merchant-wallet'] });
      setAmount('');
      setAccount('');
      setError(null);
    },
    onError: (e: unknown) => {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(
        msg === 'insufficient_balance'
          ? 'Amount exceeds your available balance.'
          : 'Request failed.',
      );
    },
  });

  const cancel = useMutation({
    meta: { successMessage: 'Payout cancelled.' },
    mutationFn: (id: string) => cancelPayout(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['merchant-payouts'] });
      qc.invalidateQueries({ queryKey: ['merchant-wallet'] });
    },
  });

  const selectedMethod = methods.find((m) => m.code === method);

  return (
    <div className="max-w-3xl space-y-4">
      <h1 className="text-2xl font-semibold">Payouts</h1>

      <Card>
        <CardHeader>
          <CardTitle>Request a cash-out</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Available: ₱{Number(wallet?.available ?? 0).toFixed(2)}
          </p>
          {methods.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No payout methods configured. Contact the administrator.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-sm font-medium">Amount (₱)</label>
                  <Input value={amount} onChange={(e) => setAmount(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Method</label>
                  <Select value={method} onValueChange={setMethod}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select…" />
                    </SelectTrigger>
                    <SelectContent>
                      {methods.map((m) => (
                        <SelectItem key={m.code} value={m.code}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {selectedMethod && (
                <div className="space-y-1">
                  <label className="text-sm font-medium">{selectedMethod.accountLabel}</label>
                  <Input
                    value={account}
                    onChange={(e) => setAccount(e.target.value)}
                    placeholder={selectedMethod.accountPlaceholder}
                  />
                </div>
              )}
              {preview && (
                <div className="rounded border p-3 text-sm space-y-1 bg-muted/30">
                  {/* Follow the server's applied sequence — the tenant's global
                      deduction order governs merchant cash-outs too. */}
                  {preview.steps && preview.steps.length > 0 ? (
                    preview.steps.map((s, i) => (
                      <Row key={`${s.key}-${i}`} label={s.label} value={s.amount} />
                    ))
                  ) : (
                    <>
                      <Row label="Service charge" value={preview.serviceCharge} />
                      <Row label="Admin fee" value={preview.adminFee} />
                      <Row label="Withholding tax" value={preview.withholdingTax} />
                    </>
                  )}
                  <div className="flex justify-between font-semibold border-t pt-1">
                    <span>You receive</span>
                    <span className="tabular-nums">₱{Number(preview.net).toFixed(2)}</span>
                  </div>
                </div>
              )}
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button
                disabled={!amount || !method || submit.isPending}
                onClick={() => submit.mutate()}
              >
                {submit.isPending ? 'Submitting…' : 'Request payout'}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>History</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {requests.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground text-sm">No requests yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b text-left text-muted-foreground">
                <tr>
                  <th className="p-3">Amount</th>
                  <th className="p-3">Net</th>
                  <th className="p-3">Method</th>
                  <th className="p-3">Status</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {requests.map((r) => (
                  <tr key={r.id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="p-3 tabular-nums">₱{Number(r.amount).toFixed(2)}</td>
                    <td className="p-3 tabular-nums">₱{Number(r.netAmount).toFixed(2)}</td>
                    <td className="p-3">{r.method}</td>
                    <td className="p-3">
                      <Badge variant={payoutStatusVariant(r.status)} className="capitalize">
                        {r.status}
                      </Badge>
                    </td>
                    <td className="p-3 text-right">
                      {r.status === 'requested' && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={cancel.isPending}
                          onClick={() => cancel.mutate(r.id)}
                        >
                          Cancel
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-muted-foreground">
      <span>{label}</span>
      <span className="tabular-nums">₱{Number(value).toFixed(2)}</span>
    </div>
  );
}

function payoutStatusVariant(status: string): 'success' | 'destructive' | 'muted' | 'secondary' {
  switch (status) {
    case 'paid':
      return 'success';
    case 'rejected':
      return 'destructive';
    case 'cancelled':
      return 'muted';
    default:
      return 'secondary';
  }
}
